import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { JawaWordmark } from "@/components/Logo";

const EMPTY_PRODUCT = {
  title: "",
  category: "collection",
  audience: "general",
  letter: "",
  description: "",
  price: "",
  currency: "GBP",
  checkout: "paystack",
  compare_price: "",
  image_url: "",
  is_active: true,
};

const EMPTY_TOPIC = {
  letter: "A",
  title: "",
  audience: "general",
  summary: "",
  link: "",
  cta: "",
  status: "live",
};

const EMPTY_DESTINATION = {
  slug: "",
  name: "",
  region: "",
  tagline: "",
  overview: "",
  image_url: "",
  places: "",
  experiences: "",
  practical: "",
  status: "live",
  featured: false,
};

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const AUDIENCES = ["general", "investor", "tourist", "returnee", "expat"];

const PLAN_CURRENCIES = ["GBP", "USD", "EUR", "NGN"];

const PlanRow = ({ plan, onSave, inputCls }) => {
  const [prices, setPrices] = useState({ ...plan.prices });
  return (
    <div className="border border-jawa-stone bg-white p-5" data-testid={`admin-plan-${plan.audience}-${plan.period}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="font-serif text-lg text-jawa-espresso">
          {plan.audience} <span className="text-sm text-jawa-espresso/50">({plan.period})</span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {PLAN_CURRENCIES.map((c) => (
            <label key={c} className="flex items-center gap-1.5 text-xs text-jawa-espresso/60">
              {c}
              <input
                type="number"
                step="0.01"
                value={prices[c] ?? ""}
                onChange={(e) => setPrices({ ...prices, [c]: e.target.value })}
                data-testid={`admin-plan-price-${plan.audience}-${plan.period}-${c.toLowerCase()}`}
                className={`${inputCls} w-24 px-2 py-1.5`}
              />
            </label>
          ))}
          <button
            onClick={() => onSave(plan, Object.fromEntries(Object.entries(prices).map(([k, v]) => [k, Number(v)])))}
            data-testid={`admin-plan-save-${plan.audience}-${plan.period}`}
            className="bg-jawa-espresso px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory hover:bg-jawa-terracotta"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("products");
  const [bookings, setBookings] = useState([]);
  const [products, setProducts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState({ whatsapp_community_link: "", consultation_meeting_link: "", events_link: "", vip_detty_season_start: "", vip_detty_season_end: "", rates: {} });
  const [savingSettings, setSavingSettings] = useState(false);
  const [currentRates, setCurrentRates] = useState({});
  const [emergencyText, setEmergencyText] = useState("");
  const [savingEmergency, setSavingEmergency] = useState(false);
  const [statsText, setStatsText] = useState("");
  const [savingStats, setSavingStats] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);
  const [editingDest, setEditingDest] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [topicForm, setTopicForm] = useState(EMPTY_TOPIC);
  const [destForm, setDestForm] = useState(EMPTY_DESTINATION);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      await api.get("/auth/me");
      const [p, e, o, t, d, l, ms, pl, bk, st, rt] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/enquiries"),
        api.get("/admin/orders"),
        api.get("/admin/topics"),
        api.get("/admin/destinations"),
        api.get("/admin/leads"),
        api.get("/admin/memberships"),
        api.get("/admin/plans"),
        api.get("/admin/bookings"),
        api.get("/admin/settings"),
        api.get("/rates"),
      ]);
      setProducts(p.data);
      setEnquiries(e.data);
      setOrders(o.data);
      setTopics(t.data);
      setDestinations(d.data);
      setLeads(l.data);
      setMemberships(ms.data);
      setPlans(pl.data);
      setBookings(bk.data);
      setSettings({
        whatsapp_community_link: st.data.whatsapp_community_link || "",
        consultation_meeting_link: st.data.consultation_meeting_link || "",
        events_link: st.data.events_link || "",
        vip_detty_season_start: st.data.vip_detty_season_start || "",
        vip_detty_season_end: st.data.vip_detty_season_end || "",
        rates: st.data.rates || {},
      });
      setCurrentRates(rt.data.rates || {});
      setEmergencyText(
        (st.data.emergency_contacts || [])
          .map((c) => [c.country, c.label, c.number, c.note].filter((x) => x != null && x !== "").join(" | "))
          .join("\n")
      );
      setStatsText(
        (st.data.impact_stats || [])
          .map((s) => `${s.value} | ${s.label}`)
          .join("\n")
      );
    } catch {
      localStorage.removeItem("jawa_admin_token");
      navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    localStorage.removeItem("jawa_admin_token");
    navigate("/admin/login");
  };

  // ---------- Products ----------
  const startEdit = (p) => {
    setEditing(p._id);
    setForm({
      title: p.title,
      category: p.category,
      audience: p.audience || "general",
      letter: p.letter || "",
      description: p.description || "",
      price: p.price ?? "",
      currency: p.currency || "GBP",
      checkout: p.checkout || "paystack",
      compare_price: p.compare_price ?? "",
      image_url: p.image_url || "",
      is_active: p.is_active,
    });
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: form.price === "" ? null : Number(form.price),
      compare_price: form.compare_price === "" ? null : Number(form.compare_price),
    };
    try {
      if (editing) {
        await api.put(`/admin/products/${editing}`, payload);
        toast.success("Product updated.");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created.");
      }
      setEditing(null);
      setForm(EMPTY_PRODUCT);
      load();
    } catch {
      toast.error("Could not save product.");
    }
  };

  const removeProduct = async (id) => {
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted.");
    if (editing === id) {
      setEditing(null);
      setForm(EMPTY_PRODUCT);
    }
    load();
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post(`/admin/products/${editing}/file`, data);
      toast.success(`PDF attached: ${res.data.filename}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // ---------- Topics ----------
  const startEditTopic = (t) => {
    setEditingTopic(t._id);
    setTopicForm({
      letter: t.letter || "A",
      title: t.title,
      audience: t.audience || "general",
      summary: t.summary || "",
      link: t.link || "",
      cta: t.cta || "",
      status: t.status || "live",
    });
  };

  const saveTopic = async (e) => {
    e.preventDefault();
    try {
      if (editingTopic) {
        await api.put(`/admin/topics/${editingTopic}`, topicForm);
        toast.success("Topic updated.");
      } else {
        await api.post("/admin/topics", topicForm);
        toast.success("Topic created.");
      }
      setEditingTopic(null);
      setTopicForm(EMPTY_TOPIC);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeTopic = async (id) => {
    await api.delete(`/admin/topics/${id}`);
    toast.success("Topic deleted.");
    if (editingTopic === id) {
      setEditingTopic(null);
      setTopicForm(EMPTY_TOPIC);
    }
    load();
  };

  // ---------- Destinations ----------
  const startEditDest = (d) => {
    setEditingDest(d._id);
    setDestForm({
      slug: d.slug,
      name: d.name,
      region: d.region || "",
      tagline: d.tagline || "",
      overview: d.overview || "",
      image_url: d.image_url || "",
      places: (d.places || []).join("\n"),
      experiences: (d.experiences || []).join("\n"),
      practical: (d.practical || []).join("\n"),
      status: d.status || "live",
      featured: Boolean(d.featured),
    });
  };

  const saveDest = async (e) => {
    e.preventDefault();
    const payload = {
      ...destForm,
      places: destForm.places.split("\n").map((s) => s.trim()).filter(Boolean),
      experiences: destForm.experiences.split("\n").map((s) => s.trim()).filter(Boolean),
      practical: destForm.practical.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editingDest) {
        await api.put(`/admin/destinations/${editingDest}`, payload);
        toast.success("Destination updated.");
      } else {
        await api.post("/admin/destinations", payload);
        toast.success("Destination created.");
      }
      setEditingDest(null);
      setDestForm(EMPTY_DESTINATION);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeDest = async (id) => {
    await api.delete(`/admin/destinations/${id}`);
    toast.success("Destination deleted.");
    if (editingDest === id) {
      setEditingDest(null);
      setDestForm(EMPTY_DESTINATION);
    }
    load();
  };

  const setEnquiryStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status });
    load();
  };

  const activateMembership = async (id) => {
    try {
      await api.post(`/admin/memberships/${id}/activate`);
      toast.success("Membership activated — confirmation email sent to the member.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const activateOrder = async (sessionId) => {
    try {
      await api.post(`/admin/orders/${sessionId}/activate`);
      toast.success("Order activated — download email sent to the customer.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePlan = async (plan, prices) => {
    try {
      await api.put(`/admin/plans/${plan.id}`, { prices });
      toast.success("Pricing updated.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---------- Settings ----------
  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", {
        whatsapp_community_link: settings.whatsapp_community_link,
        consultation_meeting_link: settings.consultation_meeting_link,
        events_link: settings.events_link,
        vip_detty_season_start: settings.vip_detty_season_start,
        vip_detty_season_end: settings.vip_detty_season_end,
        ngn_per_usd: Number(settings.rates?.ngn_per_usd) || 0,
        ngn_per_gbp: Number(settings.rates?.ngn_per_gbp) || 0,
        ngn_per_eur: Number(settings.rates?.ngn_per_eur) || 0,
      });
      toast.success("Settings saved.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  const saveEmergency = async () => {
    setSavingEmergency(true);
    try {
      const res = await api.put("/admin/emergency", { text: emergencyText });
      toast.success(`Emergency contacts saved (${res.data.count}).`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingEmergency(false);
    }
  };

  const saveStats = async () => {
    setSavingStats(true);
    try {
      const res = await api.put("/admin/stats", { text: statsText });
      toast.success(`Impact statistics saved (${res.data.count}).`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingStats(false);
    }
  };

  const editingProduct = products.find((p) => p._id === editing);
  const productTitle = (id) => products.find((p) => p._id === id)?.title || "Guide";

  const inputCls =
    "w-full border border-jawa-espresso/20 bg-white px-3 py-2.5 text-sm text-jawa-espresso focus:border-jawa-terracotta focus:outline-none";

  const TABS = ["products", "memberships", "bookings", "plans", "topics", "destinations", "enquiries", "orders", "leads", "settings"];

  return (
    <div className="min-h-screen bg-jawa-ivory" data-testid="admin-dashboard">
      <header className="border-b border-jawa-stone bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <JawaWordmark dark />
          <div className="flex items-center gap-5">
            <a href="/" data-testid="admin-view-site" className="text-xs uppercase tracking-[0.2em] text-jawa-espresso/60 hover:text-jawa-terracotta">
              View site
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="border border-jawa-espresso px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso hover:bg-jawa-espresso hover:text-jawa-ivory"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`px-5 py-3 text-xs font-semibold uppercase tracking-[0.25em] transition-colors ${
                tab === t ? "bg-jawa-espresso text-jawa-ivory" : "border border-jawa-espresso/20 text-jawa-espresso/60 hover:text-jawa-espresso"
              }`}
            >
              {t} {t === "enquiries" && enquiries.length > 0 ? `(${enquiries.length})` : ""}
              {t === "leads" && leads.length > 0 ? `(${leads.length})` : ""}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="mt-8 space-y-3" data-testid="admin-bookings-list">
            <p className="text-xs uppercase tracking-[0.2em] text-jawa-espresso/50">
              Confirmed consultations block their calendar slots automatically. All times WAT (UTC+1).
            </p>
            {bookings.length === 0 && (
              <p className="text-sm text-jawa-espresso/50" data-testid="admin-bookings-empty">No bookings yet.</p>
            )}
            {bookings.map((b) => (
              <div key={b.id} className="border border-jawa-stone bg-white p-5" data-testid={`admin-booking-${b.reference}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg text-jawa-espresso">
                      {b.customer_name} — {b.date} · {b.start_label} WAT · {b.duration} min
                      <span className={`ml-2 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${
                        b.status === "confirmed" ? "bg-jawa-turquoise/20 text-jawa-espresso" : "bg-jawa-gold/20 text-jawa-espresso"
                      }`}>{b.status}</span>
                    </p>
                    <p className="text-xs text-jawa-espresso/50">{b.customer_email} · Ref {b.reference}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "settings" && (
          <form onSubmit={saveSettings} className="mt-8 max-w-2xl space-y-6" data-testid="admin-settings-form">
            <div className="border border-jawa-stone bg-white p-6">
              <h2 className="font-serif text-xl font-medium text-jawa-espresso">Links</h2>
              <p className="mt-1 text-xs text-jawa-espresso/50">
                The consultation meeting link goes into every booking confirmation and reminder email. All links must be full https:// URLs.
              </p>
              <div className="mt-4 space-y-3">
                <label className="block text-xs uppercase tracking-[0.15em] text-jawa-espresso/60">
                  Consultation video-call link
                  <input
                    value={settings.consultation_meeting_link}
                    onChange={(e) => setSettings({ ...settings, consultation_meeting_link: e.target.value })}
                    placeholder="https://meet.google.com/… or https://zoom.us/…"
                    data-testid="admin-settings-meeting-link"
                    className={`${inputCls} mt-1 normal-case tracking-normal`}
                  />
                </label>
                <label className="block text-xs uppercase tracking-[0.15em] text-jawa-espresso/60">
                  WhatsApp community invite link
                  <input
                    value={settings.whatsapp_community_link}
                    onChange={(e) => setSettings({ ...settings, whatsapp_community_link: e.target.value })}
                    placeholder="https://chat.whatsapp.com/…"
                    data-testid="admin-settings-community-link"
                    className={`${inputCls} mt-1 normal-case tracking-normal`}
                  />
                </label>
                <label className="block text-xs uppercase tracking-[0.15em] text-jawa-espresso/60">
                  Events link
                  <input
                    value={settings.events_link}
                    onChange={(e) => setSettings({ ...settings, events_link: e.target.value })}
                    placeholder="https://…"
                    data-testid="admin-settings-events-link"
                    className={`${inputCls} mt-1 normal-case tracking-normal`}
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs uppercase tracking-[0.15em] text-jawa-espresso/60">
                    Detty December season starts (MM-DD)
                    <input
                      value={settings.vip_detty_season_start}
                      onChange={(e) => setSettings({ ...settings, vip_detty_season_start: e.target.value })}
                      placeholder="12-01"
                      data-testid="admin-settings-season-start"
                      className={`${inputCls} mt-1 normal-case tracking-normal`}
                    />
                  </label>
                  <label className="block text-xs uppercase tracking-[0.15em] text-jawa-espresso/60">
                    Season ends (MM-DD)
                    <input
                      value={settings.vip_detty_season_end}
                      onChange={(e) => setSettings({ ...settings, vip_detty_season_end: e.target.value })}
                      placeholder="01-15"
                      data-testid="admin-settings-season-end"
                      className={`${inputCls} mt-1 normal-case tracking-normal`}
                    />
                  </label>
                </div>
              </div>
            </div>
            <div className="border border-jawa-stone bg-white p-6">
              <h2 className="font-serif text-xl font-medium text-jawa-espresso">Exchange rates (₦ per unit)</h2>
              <p className="mt-1 text-xs text-jawa-espresso/50">
                Used to charge naira at checkout and show indicative GBP/EUR/USD prices. Leave blank to keep the current value.
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {["ngn_per_gbp", "ngn_per_usd", "ngn_per_eur"].map((k) => (
                  <label key={k} className="block text-xs uppercase tracking-[0.15em] text-jawa-espresso/60">
                    ₦ / {k.replace("ngn_per_", "").toUpperCase()}
                    <input
                      type="number"
                      step="0.01"
                      value={settings.rates?.[k] ?? ""}
                      placeholder={currentRates[k] ? String(currentRates[k]) : ""}
                      onChange={(e) => setSettings({ ...settings, rates: { ...settings.rates, [k]: e.target.value } })}
                      data-testid={`admin-settings-rate-${k}`}
                      className={`${inputCls} mt-1 normal-case tracking-normal`}
                    />
                  </label>
                ))}
              </div>
            </div>
            <div className="border border-jawa-stone bg-white p-6" data-testid="admin-stats-editor">
              <h2 className="font-serif text-xl font-medium text-jawa-espresso">Impact statistics</h2>
              <p className="mt-1 text-xs text-jawa-espresso/50">
                Shown beneath the homepage hero, on About and above the footer. Exactly four, one per line:
                <span className="font-mono"> Value | Label</span> — e.g. <span className="font-mono">70+ | Guests personally hosted</span>.
                Publish figures you can stand behind only.
              </p>
              <textarea
                rows={5}
                value={statsText}
                onChange={(e) => setStatsText(e.target.value)}
                placeholder={"70+ | Guests personally hosted\n36 + FCT | States covered"}
                data-testid="admin-stats-text"
                className={`${inputCls} mt-3 resize-y font-mono text-xs normal-case tracking-normal`}
              />
              <button
                type="button"
                onClick={saveStats}
                disabled={savingStats}
                data-testid="admin-stats-save"
                className="mt-3 bg-jawa-espresso px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-terracotta disabled:opacity-60"
              >
                {savingStats ? "Saving…" : "Save statistics"}
              </button>
            </div>
            <div className="border border-jawa-stone bg-white p-6" data-testid="admin-emergency-editor">
              <h2 className="font-serif text-xl font-medium text-jawa-espresso">Emergency contacts</h2>
              <p className="mt-1 text-xs text-jawa-espresso/50">
                Shown on the public /emergency page. One contact per line:
                <span className="font-mono"> Country | Service label | Number | Optional note</span>.
                Publish verified numbers only.
              </p>
              <textarea
                rows={10}
                value={emergencyText}
                onChange={(e) => setEmergencyText(e.target.value)}
                data-testid="admin-emergency-text"
                className={`${inputCls} mt-3 resize-y font-mono text-xs normal-case tracking-normal`}
              />
              <button
                type="button"
                onClick={saveEmergency}
                disabled={savingEmergency}
                data-testid="admin-emergency-save"
                className="mt-3 bg-jawa-espresso px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-terracotta disabled:opacity-60"
              >
                {savingEmergency ? "Saving…" : "Save emergency contacts"}
              </button>
            </div>
            <button
              type="submit"
              disabled={savingSettings}
              data-testid="admin-settings-save"
              className="bg-jawa-espresso px-8 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors hover:bg-jawa-terracotta disabled:opacity-60"
            >
              {savingSettings ? "Saving…" : "Save settings"}
            </button>
          </form>
        )}

        {tab === "memberships" && (
          <div className="mt-8 space-y-3" data-testid="admin-memberships-list">
            <p className="text-xs uppercase tracking-[0.2em] text-jawa-espresso/50">
              Verify the bank transfer has arrived, then activate — the member gets a confirmation email and instant access.
            </p>
            {memberships.map((m) => (
              <div key={m.id} className="border border-jawa-stone bg-white p-5" data-testid={`admin-membership-${m.reference}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg text-jawa-espresso">
                      {m.member_name} — {m.audience} ({m.period})
                      <span className={`ml-2 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${
                        m.status === "active" ? "bg-jawa-turquoise/20 text-jawa-espresso" : "bg-jawa-gold/20 text-jawa-espresso"
                      }`}>{m.status}</span>
                    </p>
                    <p className="text-xs text-jawa-espresso/50">
                      {m.member_email} · {m.currency} {m.amount} · ref {m.reference} · {m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}
                      {m.end_date ? ` · until ${new Date(m.end_date).toLocaleDateString()}` : ""}
                    </p>
                  </div>
                  {m.status === "pending" && (
                    <button
                      onClick={() => activateMembership(m.id)}
                      data-testid={`admin-activate-membership-${m.reference}`}
                      className="bg-jawa-turquoise px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-nearblack hover:bg-jawa-espresso hover:text-jawa-ivory"
                    >
                      Verify & Activate
                    </button>
                  )}
                </div>
              </div>
            ))}
            {memberships.length === 0 && <p className="text-sm text-jawa-espresso/50">No membership requests yet.</p>}
          </div>
        )}

        {tab === "plans" && (
          <div className="mt-8 space-y-3" data-testid="admin-plans-list">
            <p className="text-xs uppercase tracking-[0.2em] text-jawa-espresso/50">
              Edit membership pricing per currency. Changes go live immediately.
            </p>
            {plans.map((pl) => (
              <PlanRow key={pl.id} plan={pl} onSave={savePlan} inputCls={inputCls} />
            ))}
          </div>
        )}

        {tab === "products" && (
          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_22rem]">
            <div className="space-y-3" data-testid="admin-products-list">
              {products.map((p) => (
                <div key={p._id} className="flex items-center justify-between gap-4 border border-jawa-stone bg-white p-4">
                  <div>
                    <p className="font-serif text-lg text-jawa-espresso">
                      {p.title}
                      {p.category === "free" && (
                        <span className="ml-2 bg-jawa-turquoise px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-jawa-nearblack">Free</span>
                      )}
                      {p.category === "collection" && (
                        <span className="ml-2 border border-jawa-gold px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-jawa-gold">
                          {p.audience}
                        </span>
                      )}
                      {!p.is_active && (
                        <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-jawa-espresso/40">Hidden</span>
                      )}
                    </p>
                    <p className="flex items-center gap-2 text-xs text-jawa-espresso/50">
                      {p.price != null ? `£${p.price}` : "Price TBC"}
                      {p.pdf_filename && (
                        <span className="flex items-center gap-1 text-jawa-terracotta">
                          <FileText className="h-3 w-3" /> {p.pdf_filename}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(p)}
                      data-testid={`admin-edit-product-${p._id}`}
                      className="border border-jawa-espresso/20 p-2 text-jawa-espresso/70 hover:border-jawa-terracotta hover:text-jawa-terracotta"
                      aria-label={`Edit ${p.title}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => removeProduct(p._id)}
                      data-testid={`admin-delete-product-${p._id}`}
                      className="border border-jawa-espresso/20 p-2 text-jawa-espresso/70 hover:border-red-700 hover:text-red-700"
                      aria-label={`Delete ${p.title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              {products.length === 0 && <p className="text-sm text-jawa-espresso/50">No products yet.</p>}
            </div>

            <form onSubmit={saveProduct} data-testid="admin-product-form" className="h-fit border border-jawa-stone bg-white p-6">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-espresso">
                <Plus className="h-4 w-4" /> {editing ? "Edit product" : "New product"}
              </p>
              <div className="mt-5 space-y-3">
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid="admin-product-title" className={inputCls} />
                <div className="grid grid-cols-2 gap-3">
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-product-category" className={inputCls}>
                    <option value="free">Free guide</option>
                    <option value="collection">A–Z Collection</option>
                    <option value="guide">Paid guide (PDF)</option>
                    <option value="bundle">Bundle</option>
                    <option value="service">Service / itinerary</option>
                    <option value="vip">VIP arrival product</option>
                    <option value="merch">Shop merchandise</option>
                  </select>
                  <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="admin-product-audience" className={inputCls}>
                    {AUDIENCES.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" data-testid="admin-product-description" className={`${inputCls} resize-none`} />
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price (blank = TBC)" data-testid="admin-product-price" className={inputCls} />
                  <input type="number" step="0.01" value={form.compare_price} onChange={(e) => setForm({ ...form, compare_price: e.target.value })} placeholder="Was price (optional)" data-testid="admin-product-compare-price" className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="admin-product-currency" className={inputCls} aria-label="Base currency">
                    <option value="GBP">Base: GBP £</option>
                    <option value="USD">Base: USD $</option>
                    <option value="EUR">Base: EUR €</option>
                    <option value="NGN">Base: NGN ₦</option>
                  </select>
                  <select value={form.checkout} onChange={(e) => setForm({ ...form, checkout: e.target.value })} data-testid="admin-product-checkout" className={inputCls} aria-label="Checkout mode">
                    <option value="paystack">Sell online (Paystack)</option>
                    <option value="enquiry">Enquiry / quote first</option>
                  </select>
                </div>
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL" data-testid="admin-product-image" className={inputCls} />
                {editing && (
                  <div className="border border-dashed border-jawa-espresso/25 p-3">
                    <p className="text-xs uppercase tracking-[0.2em] text-jawa-espresso/60">
                      PDF file {editingProduct?.pdf_filename ? `— ${editingProduct.pdf_filename}` : "— none attached"}
                    </p>
                    <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-terracotta hover:text-jawa-espresso">
                      <Upload className="h-4 w-4" />
                      {uploading ? "Uploading…" : "Upload PDF"}
                      <input type="file" accept="application/pdf" onChange={uploadPdf} data-testid="admin-product-pdf-upload" className="hidden" />
                    </label>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-jawa-espresso/70">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="admin-product-active" />
                  Visible on site
                </label>
                <div className="flex gap-3 pt-2">
                  <button type="submit" data-testid="admin-product-save" className="flex-1 bg-jawa-espresso px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory hover:bg-jawa-terracotta">
                    {editing ? "Save changes" : "Create product"}
                  </button>
                  {editing && (
                    <button
                      type="button"
                      onClick={() => { setEditing(null); setForm(EMPTY_PRODUCT); }}
                      data-testid="admin-product-cancel"
                      className="border border-jawa-espresso/20 px-4 py-3 text-xs uppercase tracking-[0.2em] text-jawa-espresso/60"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        )}

        {tab === "topics" && (
          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_22rem]">
            <div className="space-y-2" data-testid="admin-topics-list">
              {topics.map((t) => (
                <div key={t._id} className="flex items-center justify-between gap-4 border border-jawa-stone bg-white p-4">
                  <div className="flex items-center gap-4">
                    <span className="flex h-9 w-9 items-center justify-center border border-jawa-gold/50 font-serif text-lg text-jawa-gold">{t.letter}</span>
                    <div>
                      <p className="font-serif text-lg text-jawa-espresso">
                        {t.title}
                        <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-jawa-espresso/40">{t.audience}</span>
                        {t.status === "planned" && (
                          <span className="ml-2 border border-jawa-yellow/60 bg-jawa-yellow/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-jawa-espresso/60">In preparation</span>
                        )}
                      </p>
                      <p className="text-xs text-jawa-espresso/50">{t.summary}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startEditTopic(t)} data-testid={`admin-edit-topic-${t._id}`} className="border border-jawa-espresso/20 p-2 text-jawa-espresso/70 hover:border-jawa-terracotta hover:text-jawa-terracotta" aria-label={`Edit ${t.title}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => removeTopic(t._id)} data-testid={`admin-delete-topic-${t._id}`} className="border border-jawa-espresso/20 p-2 text-jawa-espresso/70 hover:border-red-700 hover:text-red-700" aria-label={`Delete ${t.title}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              {topics.length === 0 && <p className="text-sm text-jawa-espresso/50">No topics yet.</p>}
            </div>

            <form onSubmit={saveTopic} data-testid="admin-topic-form" className="h-fit border border-jawa-stone bg-white p-6">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-espresso">
                <Plus className="h-4 w-4" /> {editingTopic ? "Edit topic" : "New topic"}
              </p>
              <div className="mt-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <select value={topicForm.letter} onChange={(e) => setTopicForm({ ...topicForm, letter: e.target.value })} data-testid="admin-topic-letter" className={inputCls}>
                    {LETTERS.map((L) => (
                      <option key={L} value={L}>{L}</option>
                    ))}
                  </select>
                  <select value={topicForm.audience} onChange={(e) => setTopicForm({ ...topicForm, audience: e.target.value })} data-testid="admin-topic-audience" className={inputCls}>
                    {AUDIENCES.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
                <input required value={topicForm.title} onChange={(e) => setTopicForm({ ...topicForm, title: e.target.value })} placeholder="Topic title" data-testid="admin-topic-title" className={inputCls} />
                <textarea rows={2} value={topicForm.summary} onChange={(e) => setTopicForm({ ...topicForm, summary: e.target.value })} placeholder="Short summary" data-testid="admin-topic-summary" className={`${inputCls} resize-none`} />
                <input value={topicForm.link} onChange={(e) => setTopicForm({ ...topicForm, link: e.target.value })} placeholder="External link (optional — blank = WhatsApp)" data-testid="admin-topic-link" className={inputCls} />
                <input value={topicForm.cta} onChange={(e) => setTopicForm({ ...topicForm, cta: e.target.value })} placeholder="Link label (e.g. Book Here)" data-testid="admin-topic-cta" className={inputCls} />
                <select value={topicForm.status} onChange={(e) => setTopicForm({ ...topicForm, status: e.target.value })} data-testid="admin-topic-status" className={inputCls}>
                  <option value="live">Live</option>
                  <option value="planned">In preparation</option>
                </select>
                <div className="flex gap-3 pt-2">
                  <button type="submit" data-testid="admin-topic-save" className="flex-1 bg-jawa-espresso px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory hover:bg-jawa-terracotta">
                    {editingTopic ? "Save changes" : "Create topic"}
                  </button>
                  {editingTopic && (
                    <button type="button" onClick={() => { setEditingTopic(null); setTopicForm(EMPTY_TOPIC); }} data-testid="admin-topic-cancel" className="border border-jawa-espresso/20 px-4 py-3 text-xs uppercase tracking-[0.2em] text-jawa-espresso/60">
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        )}

        {tab === "destinations" && (
          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_22rem]">
            <div className="space-y-2" data-testid="admin-destinations-list">
              {destinations.map((d) => (
                <div key={d._id} className="flex items-center justify-between gap-4 border border-jawa-stone bg-white p-4">
                  <div>
                    <p className="font-serif text-lg text-jawa-espresso">
                      {d.name}
                      <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-jawa-espresso/40">/{d.slug}</span>
                      {d.status === "coming_soon" && (
                        <span className="ml-2 border border-jawa-yellow/60 bg-jawa-yellow/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-jawa-espresso/60">Coming soon</span>
                      )}
                      {d.featured && (
                        <span className="ml-2 border border-jawa-gold px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-jawa-gold">Featured</span>
                      )}
                    </p>
                    <p className="text-xs text-jawa-espresso/50">{d.region} · {d.tagline}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startEditDest(d)} data-testid={`admin-edit-destination-${d.slug}`} className="border border-jawa-espresso/20 p-2 text-jawa-espresso/70 hover:border-jawa-terracotta hover:text-jawa-terracotta" aria-label={`Edit ${d.name}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => removeDest(d._id)} data-testid={`admin-delete-destination-${d.slug}`} className="border border-jawa-espresso/20 p-2 text-jawa-espresso/70 hover:border-red-700 hover:text-red-700" aria-label={`Delete ${d.name}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              {destinations.length === 0 && <p className="text-sm text-jawa-espresso/50">No destinations yet.</p>}
            </div>

            <form onSubmit={saveDest} data-testid="admin-destination-form" className="h-fit border border-jawa-stone bg-white p-6">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-espresso">
                <Plus className="h-4 w-4" /> {editingDest ? "Edit destination" : "New destination"}
              </p>
              <div className="mt-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input required value={destForm.name} onChange={(e) => setDestForm({ ...destForm, name: e.target.value })} placeholder="Name" data-testid="admin-destination-name" className={inputCls} />
                  <input required value={destForm.slug} onChange={(e) => setDestForm({ ...destForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-") })} placeholder="slug" data-testid="admin-destination-slug" className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input value={destForm.region} onChange={(e) => setDestForm({ ...destForm, region: e.target.value })} placeholder="Region (e.g. Nigeria)" data-testid="admin-destination-region" className={inputCls} />
                  <input value={destForm.tagline} onChange={(e) => setDestForm({ ...destForm, tagline: e.target.value })} placeholder="Tagline" data-testid="admin-destination-tagline" className={inputCls} />
                </div>
                <textarea rows={3} value={destForm.overview} onChange={(e) => setDestForm({ ...destForm, overview: e.target.value })} placeholder="Overview" data-testid="admin-destination-overview" className={`${inputCls} resize-none`} />
                <input value={destForm.image_url} onChange={(e) => setDestForm({ ...destForm, image_url: e.target.value })} placeholder="Image URL" data-testid="admin-destination-image" className={inputCls} />
                <textarea rows={3} value={destForm.places} onChange={(e) => setDestForm({ ...destForm, places: e.target.value })} placeholder="Places to visit — one per line" data-testid="admin-destination-places" className={`${inputCls} resize-none`} />
                <textarea rows={3} value={destForm.experiences} onChange={(e) => setDestForm({ ...destForm, experiences: e.target.value })} placeholder="Experiences — one per line" data-testid="admin-destination-experiences" className={`${inputCls} resize-none`} />
                <textarea rows={3} value={destForm.practical} onChange={(e) => setDestForm({ ...destForm, practical: e.target.value })} placeholder="Practical info — one per line" data-testid="admin-destination-practical" className={`${inputCls} resize-none`} />
                <select value={destForm.status} onChange={(e) => setDestForm({ ...destForm, status: e.target.value })} data-testid="admin-destination-status" className={inputCls}>
                  <option value="live">Live</option>
                  <option value="coming_soon">Coming soon</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-jawa-espresso/70">
                  <input type="checkbox" checked={destForm.featured} onChange={(e) => setDestForm({ ...destForm, featured: e.target.checked })} data-testid="admin-destination-featured" />
                  Featured destination
                </label>
                <div className="flex gap-3 pt-2">
                  <button type="submit" data-testid="admin-destination-save" className="flex-1 bg-jawa-espresso px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory hover:bg-jawa-terracotta">
                    {editingDest ? "Save changes" : "Create destination"}
                  </button>
                  {editingDest && (
                    <button type="button" onClick={() => { setEditingDest(null); setDestForm(EMPTY_DESTINATION); }} data-testid="admin-destination-cancel" className="border border-jawa-espresso/20 px-4 py-3 text-xs uppercase tracking-[0.2em] text-jawa-espresso/60">
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="mt-8 space-y-3" data-testid="admin-enquiries-list">
            {enquiries.map((q) => (
              <div key={q._id} className="border border-jawa-stone bg-white p-5" data-testid={`admin-enquiry-${q._id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg text-jawa-espresso">{q.name}</p>
                    <p className="text-xs text-jawa-espresso/50">
                      {q.email} · {q.journey || "General"} · {new Date(q.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <select
                    value={q.status}
                    onChange={(e) => setEnquiryStatus(q._id, e.target.value)}
                    data-testid={`admin-enquiry-status-${q._id}`}
                    className="border border-jawa-espresso/20 px-3 py-2 text-xs uppercase tracking-[0.15em] text-jawa-espresso"
                  >
                    <option value="new">New</option>
                    <option value="in_progress">In progress</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/70">{q.message}</p>
              </div>
            ))}
            {enquiries.length === 0 && <p className="text-sm text-jawa-espresso/50">No enquiries yet.</p>}
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-8 space-y-3" data-testid="admin-orders-list">
            {orders.map((o) => (
              <div key={o.session_id} className="flex flex-wrap items-center justify-between gap-3 border border-jawa-stone bg-white p-5" data-testid={`admin-order-${o.session_id.slice(-8)}`}>
                <div>
                  <p className="font-serif text-lg text-jawa-espresso">{productTitle(o.product_id)}</p>
                  <p className="text-xs text-jawa-espresso/50">
                    {o.reference || `…${o.session_id.slice(-8)}`}{o.customer_email ? ` · ${o.customer_email}` : ""} · {o.created_at ? new Date(o.created_at).toLocaleString() : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-lg text-jawa-espresso">
                    {o.amount != null ? `£${(o.amount / 100).toFixed(2)}` : "—"}
                  </p>
                  <p className={`text-[10px] uppercase tracking-[0.2em] ${o.payment_status === "paid" ? "text-jawa-terracotta" : "text-jawa-espresso/40"}`}>
                    {o.status === "pending_manual" ? "awaiting transfer" : o.payment_status}
                  </p>
                  {o.status === "pending_manual" && o.payment_status !== "paid" && (
                    <button
                      onClick={() => activateOrder(o.session_id)}
                      data-testid={`admin-activate-order-${o.session_id.slice(-6)}`}
                      className="mt-2 bg-jawa-turquoise px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-jawa-nearblack hover:bg-jawa-espresso hover:text-jawa-ivory"
                    >
                      Verify & Activate
                    </button>
                  )}
                </div>
              </div>
            ))}
            {orders.length === 0 && <p className="text-sm text-jawa-espresso/50">No orders yet.</p>}
          </div>
        )}

        {tab === "leads" && (
          <div className="mt-8 space-y-3" data-testid="admin-leads-list">
            <p className="text-xs uppercase tracking-[0.2em] text-jawa-espresso/50">
              Free guide email signups — upload the free guide PDF (Products → Free guide → Upload PDF) and everyone here is ready to receive it.
            </p>
            {leads.map((l) => (
              <div key={l.email} className="flex items-center justify-between border border-jawa-stone bg-white p-4" data-testid={`admin-lead-${l.email.replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="text-sm text-jawa-espresso">{l.email}</p>
                <p className="text-xs text-jawa-espresso/50">{l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}</p>
              </div>
            ))}
            {leads.length === 0 && <p className="text-sm text-jawa-espresso/50">No signups yet.</p>}
          </div>
        )}
      </main>
    </div>
  );
}
